import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from '../config/api';
import { errorMessage } from "../utils/errorMessage";
import {
  Loader2, AlertCircle, Clock, CheckCircle2, Upload, Shield,
  FileText, ArrowUpCircle, X
} from "lucide-react";

import { LOGO_URL } from "../config/constants";

export default function ClientPortal() {
  const { token } = useParams();
  const [portal, setPortal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(null);
  const fileInputRef = useRef(null);
  const [selectedItemKey, setSelectedItemKey] = useState(null);

  useEffect(() => {
    const fetchPortal = async () => {
      try {
        const res = await axios.get(`${API}/portal/${token}`);
        setPortal(res.data);
      } catch (err) {
        setError(errorMessage(err, "Unable to load portal"));
      } finally {
        setLoading(false);
      }
    };
    fetchPortal();
  }, [token]);

  const handleUploadClick = (itemKey) => {
    setSelectedItemKey(itemKey);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedItemKey) return;

    if (file.size > 20_000_000) {
      alert("File too large. Maximum 20MB.");
      return;
    }

    setUploading(selectedItemKey);
    setUploadSuccess(null);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await axios.post(
        `${API}/portal/${token}/upload/${selectedItemKey}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (res.data.success) {
        setUploadSuccess(selectedItemKey);
        // Update the portal checklist locally
        setPortal(prev => ({
          ...prev,
          status: res.data.all_required_done ? "complete" : prev.status,
          checklist: prev.checklist.map(item =>
            item.key === selectedItemKey
              ? { ...item, status: "uploaded", uploaded_filename: file.name, uploaded_at: new Date().toISOString() }
              : item
          ),
          total_uploaded: prev.total_uploaded + 1,
        }));
        setTimeout(() => setUploadSuccess(null), 3000);
      }
    } catch (err) {
      alert(errorMessage(err, "Upload failed. Please try again."));
    } finally {
      setUploading(null);
      setSelectedItemKey(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Shared wrapper — fixed overlay for light theme
  const pageStyle = {
    position: "fixed",
    inset: 0,
    zIndex: 9999,
    background: "#FAFAF8",
    color: "#1e293b",
    overflowY: "auto",
  };

  if (loading) {
    return (
      <div style={pageStyle} className="light-theme flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={pageStyle} className="light-theme flex items-center justify-center p-4">
        <div className="max-w-md text-center space-y-4">
          <AlertCircle className="w-12 h-12 text-status-red mx-auto" />
          <h1 className="text-xl font-semibold text-txt-faint">Unable to Load</h1>
          <p className="text-txt-faint">{error}</p>
        </div>
      </div>
    );
  }

  if (portal?.status === "expired") {
    return (
      <div style={pageStyle} className="light-theme flex items-center justify-center p-4">
        <div className="max-w-md text-center space-y-4">
          <Clock className="w-12 h-12 text-status-amber mx-auto" />
          <h1 className="text-xl font-semibold text-txt-faint">Link Expired</h1>
          <p className="text-txt-faint">{portal.message}</p>
        </div>
      </div>
    );
  }

  const requiredItems = portal.checklist.filter(i => i.required);
  const optionalItems = portal.checklist.filter(i => !i.required);
  const requiredDone = requiredItems.filter(i => i.status === "uploaded").length;
  const allRequiredDone = requiredDone === requiredItems.length;

  return (
    <div style={pageStyle} className="light-theme">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={handleFileChange}
        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.tiff,.heic"
        data-testid="portal-file-input"
      />

      {/* Header */}
      <nav className="sticky top-0 z-50 w-full border-b bg-white shadow-sm">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Bonsai Law" className="w-9 h-9" />
            <div>
              <span className="text-sm font-bold text-teal block">Bonsai Law</span>
              <span className="text-xs text-txt-muted">Document Portal</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-teal" />
            <span className="text-xs text-teal font-medium">Secure Upload</span>
          </div>
        </div>
      </nav>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {/* Welcome */}
        <div className="text-center space-y-2 bg-white rounded-md p-6 border border-teal/10 shadow-sm" data-testid="portal-welcome">
          <h1 className="text-2xl font-bold text-txt-faint">Document Upload</h1>
          <p className="text-txt-faint">
            Dear <span className="font-medium">{portal.client_name}</span>, please upload the requested documents for your matter.
          </p>
          <p className="text-xs text-txt-muted">
            Ref: {portal.matter_reference} &middot; {portal.fee_earner_name}
          </p>
        </div>

        {/* Progress */}
        <div className="bg-white rounded-md p-5 border border-teal/10 shadow-sm" data-testid="portal-progress">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-txt-faint">Progress</span>
            <span className="text-sm font-bold text-teal">{requiredDone}/{requiredItems.length} required</span>
          </div>
          <div className="w-full bg-gray-100 rounded-md h-2.5">
            <div
              className="bg-teal h-2.5 rounded-md transition-all duration-500"
              style={{ width: `${requiredItems.length > 0 ? (requiredDone / requiredItems.length) * 100 : 0}%` }}
            />
          </div>
          {allRequiredDone && (
            <div className="mt-3 flex items-center gap-2 text-sm text-teal font-medium">
              <CheckCircle2 className="w-4 h-4" />
              All required documents uploaded. Thank you!
            </div>
          )}
        </div>

        {/* Required documents */}
        {requiredItems.length > 0 && (
          <div className="space-y-3">
            <h2 className="text-sm font-semibold text-txt-faint flex items-center gap-2">
              <FileText className="w-4 h-4 text-teal" />
              Required Documents
            </h2>
            {requiredItems.map(item => (
              <ChecklistItem
                key={item.key}
                item={item}
                uploading={uploading === item.key}
                uploadSuccess={uploadSuccess === item.key}
                onUpload={() => handleUploadClick(item.key)}
              />
            ))}
          </div>
        )}

        {/* Optional documents */}
        {optionalItems.length > 0 && (
          <div className="space-y-3">
            <h2 className="text-sm font-semibold text-txt-faint flex items-center gap-2">
              <FileText className="w-4 h-4 text-txt-muted" />
              Optional / If Applicable
            </h2>
            {optionalItems.map(item => (
              <ChecklistItem
                key={item.key}
                item={item}
                uploading={uploading === item.key}
                uploadSuccess={uploadSuccess === item.key}
                onUpload={() => handleUploadClick(item.key)}
              />
            ))}
          </div>
        )}

        {/* GDPR notice */}
        <div className="bg-white rounded-md p-4 border border-gray-200 text-center" data-testid="portal-gdpr-notice">
          <p className="text-[10px] text-txt-muted leading-relaxed">
            Your documents are transmitted securely and stored directly in our practice management system.
            No copies are retained on any third-party servers. For more information, please refer to our Privacy Policy.
          </p>
        </div>

        {/* Footer */}
        <div className="text-center py-4 text-xs text-txt-muted">
          <div className="flex items-center justify-center gap-2">
            <img src={LOGO_URL} alt="Bonsai Law" className="w-5 h-5 opacity-50" />
            <span>Bonsai Law &middot; Secure Document Portal</span>
          </div>
        </div>
      </main>
    </div>
  );
}

function ChecklistItem({ item, uploading, uploadSuccess, onUpload }) {
  const isUploaded = item.status === "uploaded";

  return (
    <div
      className={`bg-white rounded-md border p-4 flex items-center gap-4 transition-all ${
        isUploaded ? "border-teal/20" : "border-gray-200 hover:border-teal/30"
      }`}
      data-testid={`portal-item-${item.key}`}
    >
      {/* Status icon */}
      <div className={`w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0 ${
        isUploaded ? "bg-teal/10" : "bg-gray-50"
      }`}>
        {isUploaded ? (
          <CheckCircle2 className="w-5 h-5 text-teal" />
        ) : uploading ? (
          <Loader2 className="w-5 h-5 text-teal animate-spin" />
        ) : (
          <ArrowUpCircle className="w-5 h-5 text-gray-300" />
        )}
      </div>

      {/* Label + description */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-txt-faint">{item.label}</span>
          {item.required && !isUploaded && (
            <span className="text-[9px] bg-status-red-dim text-status-red px-1.5 py-0.5 rounded font-medium">Required</span>
          )}
        </div>
        {item.description && (
          <p className="text-xs text-txt-muted mt-0.5 truncate">{item.description}</p>
        )}
        {isUploaded && item.uploaded_filename && (
          <p className="text-xs text-teal mt-1 flex items-center gap-1">
            <FileText className="w-3 h-3" />
            {item.uploaded_filename}
          </p>
        )}
      </div>

      {/* Action */}
      <div className="flex-shrink-0">
        {isUploaded ? (
          <button
            onClick={onUpload}
            className="text-xs text-txt-muted hover:text-teal px-3 py-1.5 rounded-md border border-gray-200 hover:border-teal/30 transition-colors"
            data-testid={`portal-replace-${item.key}`}
          >
            Replace
          </button>
        ) : (
          <button
            onClick={onUpload}
            disabled={uploading}
            className="text-xs text-white bg-teal hover:bg-teal-hover px-4 py-2 rounded-md font-medium transition-colors disabled:opacity-50 flex items-center gap-1.5"
            data-testid={`portal-upload-${item.key}`}
          >
            {uploading ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <Upload className="w-3 h-3" />
            )}
            Upload
          </button>
        )}
      </div>

      {/* Success flash */}
      {uploadSuccess && (
        <div className="absolute inset-0 bg-teal/5 rounded-md flex items-center justify-center pointer-events-none animate-pulse">
          <CheckCircle2 className="w-8 h-8 text-teal" />
        </div>
      )}
    </div>
  );
}
